/**
 * Timeline colours and fonts, resolved from the design tokens (W1-15 timeline-host; tokens in
 * src/ui/styles/tokens.css and themes.css, W1-12).
 *
 * Canvas drawing cannot use `var(--token)`, so the host reads the computed custom properties of its root element
 * once per theme change (`readTimelineTheme`). Every colour comes from a token: each entry lists the preferred
 * timeline-specific token first (`--color-snap-*`, `--color-timeline-*`, `--color-waveform`, defined for every
 * theme in tokens.css / themes.css) and then the general tokens it falls back to. When no stylesheet is loaded at all (unit tests, a detached element) the last resort is a CSS
 * system colour keyword (`CanvasText`, `Canvas`, `GrayText`, `Highlight`, `Mark`), never a literal colour.
 *
 * Beat-division colours follow the osu! convention (DESIGN.md §4.6, ux-landscape §2.1): whole beat white, 1/2 red,
 * 1/3 and 1/6 purple, 1/4 blue, 1/8 yellow, 1/12 / 1/16 / finer grey, odd divisions (1/5, 1/7, 1/9) yellow.
 */

/** Colour class of a grid tick, from the reduced denominator of its position within the beat. */
export type SnapColorClass = 'whole' | 'half' | 'third' | 'quarter' | 'eighth' | 'fine' | 'odd';

export type SeverityKind = 'fatal' | 'error' | 'warning' | 'tip';

export interface TimelineTheme {
  /** Column background behind every layer. */
  readonly background: string;
  readonly gutterBackground: string;
  readonly overviewBackground: string;
  readonly text: string;
  readonly textMuted: string;
  readonly textSubtle: string;
  readonly divider: string;
  readonly border: string;
  readonly gridBar: string;
  readonly gridBeat: string;
  readonly gridSub: string;
  readonly snap: Readonly<Record<SnapColorClass, string>>;
  readonly playhead: string;
  readonly accent: string;
  readonly accentMuted: string;
  /** Translucent fill of marquees and time-range selections. */
  readonly selection: string;
  readonly marker: string;
  readonly bpm: string;
  readonly loop: string;
  readonly loopFill: string;
  readonly generator: string;
  readonly severity: Readonly<Record<SeverityKind, string>>;
  readonly noteDensity: string;
  readonly keyDensity: string;
  readonly waveform: string;
  /** The quiet waveform behind the note lane grid (neutral, UX-031). */
  readonly waveformBackdrop: string;
  readonly fontSans: string;
  readonly fontMono: string;
}

type FlatKey = Exclude<keyof TimelineTheme, 'snap' | 'severity'>;

/** Token candidates per entry, most specific first; the last string is a CSS system colour or generic font. */
const FLAT_TOKENS: Readonly<Record<FlatKey, readonly string[]>> = {
  background: ['--color-timeline-bg', '--color-bg', 'Canvas'],
  gutterBackground: ['--color-timeline-gutter', '--color-surface-1', 'Canvas'],
  overviewBackground: ['--color-timeline-overview', '--color-surface-sunken', 'Canvas'],
  text: ['--color-text', 'CanvasText'],
  textMuted: ['--color-text-muted', 'GrayText'],
  textSubtle: ['--color-text-subtle', 'GrayText'],
  divider: ['--color-divider', 'GrayText'],
  border: ['--color-border', 'GrayText'],
  gridBar: ['--color-grid-bar', 'GrayText'],
  gridBeat: ['--color-grid-beat', 'GrayText'],
  gridSub: ['--color-grid-sub', 'GrayText'],
  playhead: ['--color-playhead', '--color-danger', 'Highlight'],
  accent: ['--color-accent', 'Highlight'],
  accentMuted: ['--color-accent-muted', '--color-selection', 'Highlight'],
  selection: ['--color-selection', '--color-accent-muted', 'Highlight'],
  marker: ['--color-timeline-marker', '--color-info', 'Highlight'],
  bpm: ['--color-timeline-bpm', '--color-warning', 'Mark'],
  loop: ['--color-timeline-loop', '--color-success', 'Highlight'],
  loopFill: ['--color-timeline-loop-fill', '--color-success-muted', 'Highlight'],
  generator: ['--color-timeline-generator', '--color-badge-in-use', 'Highlight'],
  noteDensity: ['--color-timeline-note-density', '--color-note-tap', '--color-accent', 'Highlight'],
  keyDensity: ['--color-timeline-key-density', '--color-curve', '--color-accent', 'Highlight'],
  waveform: ['--color-waveform', '--color-accent', 'GrayText'],
  waveformBackdrop: ['--color-waveform-backdrop', '--color-text-subtle', 'GrayText'],
  fontSans: ['--font-sans', 'sans-serif'],
  fontMono: ['--font-mono', 'monospace'],
};

const SNAP_TOKENS: Readonly<Record<SnapColorClass, readonly string[]>> = {
  whole: ['--color-snap-1', '--color-text', 'CanvasText'],
  half: ['--color-snap-2', '--color-danger', 'Mark'],
  third: ['--color-snap-3', '--color-badge-in-use', 'Highlight'],
  quarter: ['--color-snap-4', '--color-info', 'Highlight'],
  eighth: ['--color-snap-8', '--color-warning', 'Mark'],
  fine: ['--color-snap-fine', '--color-text-subtle', 'GrayText'],
  odd: ['--color-snap-odd', '--color-warning', 'Mark'],
};

const SEVERITY_TOKENS: Readonly<Record<SeverityKind, readonly string[]>> = {
  fatal: ['--color-severity-fatal', '--color-danger', 'Mark'],
  error: ['--color-severity-error', '--color-danger', 'Mark'],
  warning: ['--color-severity-warning', '--color-warning', 'Mark'],
  tip: ['--color-severity-tip', '--color-info', 'Highlight'],
};

/** Reads a custom property; '' when it is not defined. */
export type TokenReader = (name: string) => string;

function resolve(read: TokenReader, candidates: readonly string[]): string {
  for (const c of candidates) {
    if (!c.startsWith('--')) return c;
    const v = read(c).trim();
    if (v !== '') return v;
  }
  return '';
}

function resolveRecord<K extends string>(
  read: TokenReader,
  table: Readonly<Record<K, readonly string[]>>,
): Record<K, string> {
  const out = {} as Record<K, string>;
  for (const k of Object.keys(table) as K[]) out[k] = resolve(read, table[k]);
  return out;
}

/** Builds a theme from an arbitrary token reader (tests, OffscreenCanvas workers). */
export function themeFromTokens(read: TokenReader): TimelineTheme {
  const flat = resolveRecord(read, FLAT_TOKENS);
  return Object.freeze({
    ...flat,
    snap: Object.freeze(resolveRecord(read, SNAP_TOKENS)),
    severity: Object.freeze(resolveRecord(read, SEVERITY_TOKENS)),
  });
}

/** Reads the design tokens visible at `el` (its computed style), or the system-colour fallbacks without one. */
export function readTimelineTheme(el: Element | null): TimelineTheme {
  if (el === null || typeof getComputedStyle !== 'function') return themeFromTokens(() => '');
  let style: CSSStyleDeclaration | null = null;
  try {
    style = getComputedStyle(el);
  } catch {
    style = null;
  }
  return themeFromTokens((name) => style?.getPropertyValue(name) ?? '');
}

/** The theme used before a host is mounted. */
export const FALLBACK_THEME: TimelineTheme = themeFromTokens(() => '');

/** Colour class of a reduced denominator (1 = on the beat, 2 = half beat, ...). */
export function snapColorClass(den: number): SnapColorClass {
  switch (den) {
    case 1:
      return 'whole';
    case 2:
      return 'half';
    case 3:
    case 6:
      return 'third';
    case 4:
      return 'quarter';
    case 8:
      return 'eighth';
    default:
      return den % 12 === 0 || den % 16 === 0 || den > 16 ? 'fine' : 'odd';
  }
}

/** Canvas font string, e.g. `font(theme, 10, 'mono', 600)` -> "600 10px ui-monospace, ...". */
export function canvasFont(
  theme: TimelineTheme,
  sizePx: number,
  family: 'sans' | 'mono' = 'mono',
  weight = 400,
): string {
  return `${String(weight)} ${String(sizePx)}px ${family === 'mono' ? theme.fontMono : theme.fontSans}`;
}

/** Severity of a decoration kind, or null for non-problem decorations. */
export function severityOfDecoration(kind: string): SeverityKind | null {
  switch (kind) {
    case 'problem-fatal':
      return 'fatal';
    case 'problem-error':
      return 'error';
    case 'problem-warning':
      return 'warning';
    case 'problem-tip':
      return 'tip';
    default:
      return null;
  }
}
